import React from "react";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Download, TrendingUp, TrendingDown } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import MetricsKPICards from "@/components/MetricsKPICards";
import MetricsGrowthChart from "@/components/MetricsGrowthChart";
import MetricsTopIdeas from "@/components/MetricsTopIdeas";

export default function Metrics() {
  const [realtimeKey, setRealtimeKey] = useState(0);

  const { data: metrics, isLoading: metricsLoading } = useQuery({
    queryKey: ["user-metrics", realtimeKey],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_user_metrics");
      if (error) throw error;
      return data[0];
    },
  });

  const { data: chartData, isLoading: chartLoading } = useQuery({
    queryKey: ["metrics-by-date", realtimeKey],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_metrics_by_date");
      if (error) throw error;
      return data;
    },
  });

  const { data: topIdeas, isLoading: topIdeasLoading } = useQuery({
    queryKey: ["top-ideas", realtimeKey],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_top_ideas");
      if (error) throw error;
      return data;
    },
  });

  // Real-time subscriptions
  useEffect(() => {
    const outreachChannel = supabase
      .channel("metrics-outreach")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "outreach",
        },
        () => {
          console.log("Outreach changed, refreshing metrics");
          setRealtimeKey((prev) => prev + 1);
        }
      )
      .subscribe();

    const milestonesChannel = supabase
      .channel("metrics-milestones")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "milestones",
        },
        () => {
          console.log("Milestones changed, refreshing metrics");
          setRealtimeKey((prev) => prev + 1);
        }
      )
      .subscribe();

    const progressChannel = supabase
      .channel("metrics-progress")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "progress_logs",
        },
        () => {
          console.log("Progress logs changed, refreshing metrics");
          setRealtimeKey((prev) => prev + 1);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(outreachChannel);
      supabase.removeChannel(milestonesChannel);
      supabase.removeChannel(progressChannel);
    };
  }, []);

  const handleExportCSV = () => {
    if (!metrics || !chartData) return;

    const csvRows = [
      ["Metric", "Value"],
      ["Total Ideas", metrics.total_ideas],
      ["Total Decks", metrics.total_decks],
      ["Total Investors", metrics.total_investors],
      ["Total Outreach", metrics.total_outreach],
      ["Total Opens", metrics.total_opens],
      ["Total Replies", metrics.total_replies],
      ["Total Meetings", metrics.total_meetings],
      ["Milestones Done", metrics.milestones_done],
      ["Total Deck Views", metrics.total_deck_views],
      [""],
      ["Date", "New Ideas", "New Outreach", "New Milestones", "New Views"],
      ...chartData.map((d) => [
        d.date,
        d.new_ideas,
        d.new_outreach,
        d.new_milestones,
        d.new_deck_views,
      ]),
    ];

    const csvContent = csvRows.map((row) => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `metrics-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);

    toast({ title: "CSV exported successfully" });
  };

  const handleExportPDF = () => {
    window.print();
    toast({ title: "Print dialog opened for PDF export" });
  };

  if (metricsLoading || chartLoading || topIdeasLoading) {
    return (
      <div className="container mx-auto p-6 max-w-7xl">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-muted rounded w-1/4"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-32 bg-muted rounded"></div>
            ))}
          </div>
          <div className="h-96 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  const hasData =
    metrics &&
    (metrics.total_ideas > 0 ||
      metrics.total_outreach > 0 ||
      metrics.milestones_done > 0);

  return (
    <div className="container mx-auto p-6 max-w-7xl print:p-2">
      <div className="flex justify-between items-center mb-8 print:mb-4">
        <div>
          <h1 className="text-3xl font-bold mb-2 print:text-2xl">Traction Analytics</h1>
          <p className="text-muted-foreground print:text-xs">
            Track your startup progress and metrics
          </p>
        </div>
        <div className="flex gap-2 print:hidden">
          <Button variant="outline" onClick={handleExportCSV}>
            <Download className="mr-2 h-4 w-4" />
            Export CSV
          </Button>
          <Button variant="outline" onClick={handleExportPDF}>
            <Download className="mr-2 h-4 w-4" />
            Export PDF
          </Button>
        </div>
      </div>

      {!hasData ? (
        <Card>
          <CardContent className="py-12 text-center">
            <TrendingUp className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Start tracking your traction</h3>
            <p className="text-muted-foreground mb-4">
              Log milestones, send outreach, and watch your metrics grow
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-8 print:space-y-4">
          <MetricsKPICards metrics={metrics} chartData={chartData} />

          <Card className="print:break-inside-avoid">
            <CardHeader className="print:pb-2">
              <CardTitle>Growth Over Time</CardTitle>
            </CardHeader>
            <CardContent>
              <MetricsGrowthChart data={chartData} />
            </CardContent>
          </Card>

          <Card className="print:break-before-page">
            <CardHeader className="print:pb-2">
              <CardTitle>Top Performing Ideas</CardTitle>
            </CardHeader>
            <CardContent>
              <MetricsTopIdeas ideas={topIdeas} />
            </CardContent>
          </Card>
        </div>
      )}

      <style>{`
        @media print {
          @page {
            size: landscape;
            margin: 0.5cm;
          }
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
        }
      `}</style>
    </div>
  );
}